import React, { useState, useMemo } from 'react';
import { Menu } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { QuickCapture } from '../components/QuickCapture';
import { TodayFocus } from '../components/TodayFocus';
import { KanbanBoard } from '../components/KanbanBoard';
import { WorkspaceSettings } from '../components/WorkspaceSettings';
import { WorkspaceInfo } from '../components/WorkspaceInfo';
import { TaskFilters } from '../components/TaskFilters';
import { TagManager } from '../components/TagManager';
import { OnboardingGuide } from '../components/OnboardingGuide';
import { AIAssistant } from '../components/AIAssistant';
import { UpgradeModal } from '../components/UpgradeModal';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useI18n } from '../i18n';

export default function DashboardPage() {
  const { currentWorkspace, tasks, tags, fetchTags } = useWorkspace();
  const { t } = useI18n();
  const [currentView, setCurrentView] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState({ priorities: [], tags: [], creators: [], timeRange: 'all' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const viewLabels = {
    all: t('allTasks'),
    inbox: t('inboxTasks'),
    today: t('todayTasks'),
    starred: t('starredTasks'),
    done: t('doneTasks'),
  };

  const filteredTasks = useMemo(() => {
    let result = tasks;
    
    // View filter
    if (currentView === 'inbox') result = result.filter(t => t.status === 'inbox');
    else if (currentView === 'today') result = result.filter(t => t.is_today_focus);
    else if (currentView === 'starred') result = result.filter(t => t.is_starred);
    else if (currentView === 'done') result = result.filter(t => t.status === 'done');
    
    // Additional filters
    if (filters.priorities?.length) result = result.filter(t => filters.priorities.includes(t.priority));
    if (filters.tags?.length) result = result.filter(t => filters.tags.some(ft => t.tags?.includes(ft)));
    if (filters.creators?.length) result = result.filter(t => filters.creators.includes(t.created_by_name));
    if (filters.timeRange && filters.timeRange !== 'all') {
      const now = new Date();
      if (filters.timeRange === 'today') {
        result = result.filter(t => new Date(t.created_at).toDateString() === now.toDateString());
      } else if (filters.timeRange === 'week') {
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        result = result.filter(t => new Date(t.created_at) >= weekAgo);
      } else if (filters.timeRange === 'month') {
        const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        result = result.filter(t => new Date(t.created_at) >= monthAgo);
      }
    }
    
    return result;
  }, [tasks, currentView, filters]);

  return (
    <div className="flex h-screen bg-[#09090B]" data-testid="dashboard">
      <Sidebar 
        currentView={currentView} 
        onViewChange={setCurrentView} 
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-12 max-w-7xl">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-2">
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 -ml-2 text-zinc-400 hover:text-zinc-100 transition-colors"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-50 font-['Outfit'] tracking-tight" data-testid="workspace-title">
                {currentWorkspace?.name || 'Welcome'}
              </h1>
              <WorkspaceSettings />
              <WorkspaceInfo />
            </div>
            <p className="text-sm text-zinc-500 ml-0 lg:ml-0">
              {viewLabels[currentView]} — {filteredTasks.length} {t('tasks')}
            </p>
          </div>

          {/* Quick Capture */}
          <div className="mb-6 sm:mb-8">
            <QuickCapture />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <TaskFilters filters={filters} onFiltersChange={setFilters} tags={tags || []} />
            <TagManager tags={tags || []} onTagsChange={() => fetchTags && currentWorkspace && fetchTags(currentWorkspace.id)} />
          </div>

          {/* Today Focus */}
          {(currentView === 'all' || currentView === 'today') && (
            <div className="mb-6 sm:mb-8">
              <TodayFocus />
            </div>
          )}

          {/* Kanban Board */}
          <div>
            <KanbanBoard tasks={filteredTasks} />
          </div>
        </div>
      </main>

      {/* Onboarding */}
      <OnboardingGuide />
      
      {/* AI Assistant */}
      <AIAssistant />
      
      {/* Upgrade Modal */}
      <UpgradeModal />
    </div>
  );
}
